import React, { useState, useEffect } from 'react';
import { useStore } from '../../context/StoreContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { EnvelopeSimple, PaperPlaneTilt, CircleNotch, ShieldCheck, Warning } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SmtpSettingsPage = () => {
  const { currentStore } = useStore();
  const [settings, setSettings] = useState({
    enabled: false, host: '', port: 587, username: '',
    password: '', from_email: '', from_name: '', use_tls: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (!currentStore) return;
    axios.get(`${API_URL}/api/stores/${currentStore.id}/settings/smtp`, { withCredentials: true })
      .then(r => setSettings(prev => ({ ...prev, ...r.data })))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [currentStore]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/stores/${currentStore.id}/settings/smtp`, settings, { withCredentials: true });
      toast.success('Configuration SMTP sauvegardée');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally { setSaving(false); }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const resp = await axios.post(`${API_URL}/api/stores/${currentStore.id}/settings/smtp/test`, {}, { withCredentials: true });
      toast.success(resp.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur SMTP');
    } finally { setTesting(false); }
  };

  const update = (key, value) => setSettings(prev => ({ ...prev, [key]: value }));

  if (!currentStore) return <div className="text-center py-12"><p className="text-slate-500">Sélectionnez une boutique</p></div>;

  return (
    <div className="space-y-6 max-w-2xl" data-testid="smtp-settings-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Notifications email</h1>
        <p className="text-slate-500 mt-1">Configurez le serveur SMTP pour envoyer des emails de suivi à vos clients</p>
      </div>

      {loading ? (
        <div className="py-12 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0052FF] mx-auto" /></div>
      ) : (
        <>
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2"><EnvelopeSimple size={18} /> Serveur SMTP</CardTitle>
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-slate-500">Activer</Label>
                  <Switch checked={settings.enabled} onCheckedChange={v => update('enabled', v)} data-testid="smtp-enabled-switch" />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {!settings.enabled && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                  <Warning size={18} className="shrink-0 mt-0.5" />
                  <p>Les notifications email sont désactivées. Les changements de statut de commande ne déclencheront pas d'emails.</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Hôte SMTP</Label>
                  <Input value={settings.host} onChange={e => update('host', e.target.value)} placeholder="smtp.gmail.com" data-testid="smtp-host" />
                </div>
                <div>
                  <Label className="text-xs">Port</Label>
                  <Input type="number" value={settings.port} onChange={e => update('port', parseInt(e.target.value) || 587)} data-testid="smtp-port" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Identifiant</Label>
                  <Input value={settings.username} onChange={e => update('username', e.target.value)} placeholder="votre@email.com" data-testid="smtp-username" />
                </div>
                <div>
                  <Label className="text-xs">Mot de passe</Label>
                  <Input type="password" value={settings.password} onChange={e => update('password', e.target.value)} placeholder="••••••••" data-testid="smtp-password" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Email expéditeur</Label>
                  <Input value={settings.from_email} onChange={e => update('from_email', e.target.value)} placeholder="noreply@mrg-istres.fr" data-testid="smtp-from-email" />
                </div>
                <div>
                  <Label className="text-xs">Nom expéditeur</Label>
                  <Input value={settings.from_name} onChange={e => update('from_name', e.target.value)} placeholder="MRG Optique & Audition" data-testid="smtp-from-name" />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Switch checked={settings.use_tls} onCheckedChange={v => update('use_tls', v)} data-testid="smtp-tls-switch" />
                <Label className="text-xs">Utiliser TLS (recommandé)</Label>
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-3">
            <Button onClick={handleSave} disabled={saving} className="bg-[#0052FF]" data-testid="smtp-save-btn">
              {saving ? <CircleNotch className="animate-spin mr-2" size={14} /> : <ShieldCheck className="mr-2" size={14} />}
              Sauvegarder
            </Button>
            {settings.enabled && settings.host && (
              <Button variant="outline" onClick={handleTest} disabled={testing} data-testid="smtp-test-btn">
                {testing ? <CircleNotch className="animate-spin mr-2" size={14} /> : <PaperPlaneTilt className="mr-2" size={14} />}
                Envoyer un email de test
              </Button>
            )}
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Comment ça marche ?</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-slate-600 space-y-2">
              <p>Lorsque vous changez le statut d'une commande dans la gestion des commandes, un email est automatiquement envoyé au client avec :</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Le nouveau statut de la commande</li>
                <li>Le code de suivi unique</li>
                <li>Un récapitulatif de la commande</li>
              </ul>
              <p className="text-slate-400 text-xs mt-3">Exemples de serveurs SMTP : Gmail (smtp.gmail.com:587), OVH (ssl0.ovh.net:465), Outlook (smtp.office365.com:587)</p>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default SmtpSettingsPage;
